/**
 * Shared «cast a spell from the HUD» logic — used by the full sheet's
 * SpellsSection and by the battle HUD's spells section (CombatState).
 *
 * Attack spells post a to-hit roll via roll:request (their own chat card);
 * save/utility spells post a rich spell-cast chat card. Leveled spells spend
 * a slot through the caller-provided spendSlot (each caller already has its
 * own slot bookkeeping wired to the store + server).
 */
import type { HudSpell, HudCharacterData, AbilityStat } from '../../lib/character-hud-store';
import { helpers, statLabel } from '../../lib/character-hud-store';
import type { SpellCastInfo } from '@roll1/shared';
import type { TranslationKeys } from '../../i18n/types';

type TFn = (key: keyof TranslationKeys) => string;

export interface SpellCastCtx {
  data: HudCharacterData;
  send: (msg: unknown) => void;
  /** useRoll().rollSpellAttack — posts the to-hit roll card for attack spells. */
  rollSpellAttack: () => void;
  /** Decrement a slot of the given level (store patch + server message). */
  spendSlot: (level: number) => void;
  /** i18n translate fn (for the stat label in the fallback text). */
  t: TFn;
}

export function performSpellCast(spell: HudSpell, ctx: SpellCastCtx): void {
  const { data, send, rollSpellAttack, spendSlot, t } = ctx;
  const lvlLabel = spell.level === 0 ? 'заговор' : `${spell.level} круг`;

  if (spell.roll?.attack === 'melee' || spell.roll?.attack === 'ranged') {
    // Attack spell → posts a to-hit roll card via roll:request (its own card).
    rollSpellAttack();
    if (spell.level > 0) spendSlot(spell.level);
    return;
  }

  // Save + utility → rich spell-cast card. `text` is the plain-text fallback
  // (notifications / non-card renderers); `spellCast` drives the chat card.
  const dc = helpers.spellDC(data);
  const saveStat = spell.roll?.saveStat;
  const spellCast: SpellCastInfo = {
    name: spell.name,
    level: spell.level,
    school: spell.school,
    components: spell.components,
    concentration: spell.concentration,
    ritual: spell.ritual,
    castingTime: spell.castingTime,
    range: spell.range,
    duration: spell.duration,
    description: spell.description,
    higherLevels: spell.higherLevels,
    ...(saveStat
      ? { save: { ability: saveStat, dc, halfOnSuccess: spell.roll?.saveHalfOnSuccess } }
      : {}),
  };
  const text = saveStat
    ? `✦ ${spell.name} (${lvlLabel}) — спасбросок ${statLabel(t, saveStat as AbilityStat)} СЛ ${dc}`
    : `✦ Сотворяет: ${spell.name} (${lvlLabel})`;
  send({ type: 'chat:message', text, spellCast });

  if (spell.level > 0) spendSlot(spell.level);
}
